import React from "react";
import * as UI from "./ui.jsx";
// TV — per-room control. Channel guide is data-driven from the TV registry;
// channel and power changes show a pending → confirmed state (real sets take a second or two).

const { useState: useStateT, useRef: useRefT } = React;

function TVView() {
  const { Icon, Card, Tag, ModuleGate, MockNote } = UI;
  const { CHANNELS, ROOMS } = window.DATA;
  const [rooms, setRooms] = useStateT(() => ROOMS.map(r => ({ ...r, pending: null })));
  const [activeRoom, setActiveRoom] = useStateT(ROOMS.length ? ROOMS[0].id : null);
  const timers = useRefT({});
  const cur = rooms.find(r => r.id === activeRoom);

  const patchRoom = (id, patch) => setRooms(rs => rs.map(r => r.id === id ? { ...r, ...patch } : r));
  // pending → confirmed after ~1.6 s, like the real Exterity boxes
  const sendPending = (id, patch, label) => {
    patchRoom(id, { pending: label });
    clearTimeout(timers.current[id]);
    timers.current[id] = setTimeout(() => patchRoom(id, { ...patch, pending: null }), 1600);
  };
  const setChannel = (n) => { if (n !== cur.channel) sendPending(cur.id, { channel: n }, `Changing to ${CHANNELS.find(c => c.n === n)?.name}…`); };
  const togglePower = () => sendPending(cur.id, { tv: !cur.tv }, cur.tv ? "Powering off…" : "Powering on…");
  const setVol = (v) => patchRoom(cur.id, { vol: Math.max(0, Math.min(100, v)) });

  const channel = cur ? CHANNELS.find(c => c.n === cur.channel) : null;

  return (
    <ModuleGate modules="tv">
      <div className="fade-in">
        <div className="row" style={{ gap: 10, marginBottom: 22, flexWrap: "wrap" }}>
          {rooms.map(r => {
            const c = CHANNELS.find(x => x.n === r.channel);
            return (
              <button key={r.id} className="card tvpick" data-on={activeRoom === r.id ? "1" : undefined} onClick={() => setActiveRoom(r.id)}>
                <div className="row" style={{ justifyContent: "space-between" }}>
                  <div className="eyebrow" style={{ color: activeRoom === r.id ? "var(--inglis-blue)" : undefined }}>{r.name}</div>
                  {r.pending ? <Tag variant="warn">Sending…</Tag> : r.tv ? <Tag variant="live">On</Tag> : <Tag>Off</Tag>}
                </div>
                <div className="row" style={{ gap: 14, marginTop: 10, alignItems: "baseline" }}>
                  <div>
                    <div className="num" style={{ fontSize: 21, color: "var(--fg-1)" }}>CH {r.channel}</div>
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{c?.name}</div>
                  </div>
                  <div style={{ flex: 1 }}/>
                  <div>
                    <div className="muted" style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase" }}>Vol</div>
                    <div className="num" style={{ fontSize: 19, marginTop: 2 }}>{r.vol}</div>
                  </div>
                </div>
                <div className="muted" style={{ fontSize: 10.5, marginTop: 8 }}>{r.device}</div>
              </button>
            );
          })}
        </div>

        {cur && (
          <div className="grid tv-grid" style={{ gridTemplateColumns: "1.3fr 1fr", gap: 22 }}>
            <Card title={cur.name} right={
              <span className="row" style={{ gap: 8 }}>
                <MockNote module="tv"/>
                <button className={`btn sm ${cur.tv ? "" : "primary"}`} onClick={togglePower} disabled={!!cur.pending}>
                  <Icon.power style={{ width: 14, height: 14 }}/> {cur.pending ? "…" : cur.tv ? "On" : "Off"}
                </button>
              </span>
            }>
              <div className="tvscreen" style={{ background: cur.tv ? "var(--inglis-blue-dark)" : "#10131a" }}>
                {cur.pending ? (
                  <div style={{ textAlign: "center" }}>
                    <div className="spinner"/>
                    <div style={{ marginTop: 12, fontSize: 13, color: "rgba(255,255,255,0.85)", letterSpacing: "0.06em" }}>{cur.pending}</div>
                  </div>
                ) : cur.tv ? (
                  <>
                    <div style={{ textAlign: "center", padding: "0 30px" }}>
                      <div className="eyebrow" style={{ color: "#fff", opacity: 0.8 }}>Now showing</div>
                      <div className="display" style={{ fontSize: "clamp(26px, 3vw, 40px)", color: "#fff", marginTop: 6, lineHeight: 1.1 }}>{channel?.name}</div>
                    </div>
                    <div style={{ position: "absolute", bottom: 14, left: 18, right: 18, display: "flex", justifyContent: "space-between", fontFamily: "var(--font-num)", fontSize: 11, color: "rgba(255,255,255,0.6)" }}>
                      <span>CH {cur.channel}</span><span>VOL {cur.vol}</span>
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 14, letterSpacing: "0.15em", color: "rgba(255,255,255,0.4)" }}>· · ·   OFF   · · ·</div>
                )}
              </div>

              <div className="row" style={{ gap: 12, marginTop: 20, justifyContent: "center", flexWrap: "wrap" }}>
                <div className="row" style={{ gap: 8 }}>
                  <span className="eyebrow" style={{ fontSize: 10 }}>Volume</span>
                  <button className="btn sm" onClick={() => setVol(0)}><Icon.mute style={{ width: 14, height: 14 }}/></button>
                  <button className="btn sm" onClick={() => setVol(cur.vol - 5)}>−</button>
                  <span className="num" style={{ fontSize: 20, width: 40, textAlign: "center" }}>{cur.vol}</span>
                  <button className="btn sm" onClick={() => setVol(cur.vol + 5)}>+</button>
                </div>
              </div>
            </Card>

            <Card title="Channel guide" subtitle="From the venue TV registry — tap to switch.">
              <div className="chan-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
                {CHANNELS.map(c => (
                  <button key={c.n} className={`chan ${cur.channel === c.n ? "sel" : ""}`} onClick={() => setChannel(c.n)} disabled={!!cur.pending}>
                    <span className="num">{c.n}</span>
                    <span className="name">{c.name}</span>
                    {cur.pending && cur.pending.includes(c.name) && <span className="muted" style={{ fontSize: 10 }}>Sending…</span>}
                  </button>
                ))}
              </div>
              <div className="divider"/>
              <div className="eyebrow" style={{ marginBottom: 10 }}>Send to all TVs</div>
              <div className="row" style={{ flexWrap: "wrap", gap: 8 }}>
                {CHANNELS.slice(0, 3).map(c => (
                  <button key={c.n} className="tag" style={{ cursor: "pointer", padding: "8px 14px" }}
                    onClick={() => rooms.forEach(r => sendPending(r.id, { channel: c.n, tv: true }, `Changing to ${c.name}…`))}>
                    {c.name}
                  </button>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>
    </ModuleGate>
  );
}

export { TVView };
